import React, { useEffect, useState, useCallback } from "react";
import { useMsal } from "@azure/msal-react";
import { useLocation, useNavigate } from "react-router-dom";
import { loginRequest } from "./authConfig";
import { APP_VERSION } from "./config/appConfig";
import RackStarLogoLight from "./logos/RackStarLogo3.png";
import RackStarLogoDark from "./logos/RackStarLogo2.png";
import {
  Box,
  CssBaseline,
  Toolbar,
  AppBar,
  Typography,
  IconButton,
  Tooltip,
  Menu,
  Divider,
  MenuItem,
  ListItemIcon,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import MenuOpenIcon from "@mui/icons-material/MenuOpen";
import Profile from "./components/Profile";
import Sidebar from "./components/Features/Sidebar";
import { DarkModeSwitch } from "./common/components/DarkModeSwitch";
import { LogOutIcon } from "lucide-react";
import ConfirmDialogManager from "./common/components/ConfirmDialogManager";
import { setItem } from "./common/services/storage-service";
import GlobalSearchBar from "./common/components/GlobalSearch";
import FontSizeControl from "./common/components/FontSizeControl";
import AIChatBot from "./components/AIChatBot";
import CustomRoutes from "./components/Routes";
import { Logout } from "@mui/icons-material";
import PersonIcon from "@mui/icons-material/Person";
import logger from "./helpers/logger";
import AuthStatus from "./components/AuthStatus";
import cookieService from "./common/services/cookie-service";
import { InteractionRequiredAuthError } from "@azure/msal-browser";
import { ROUTES } from "./components/Routes/routes";
import { logoutUser } from "./common/interceptor/msal-instance";
import { cancelAllRequests } from "./common/interceptor/axios-interceptor";
import { SIDEBAR_OPEN } from "./enums";
import axios from "axios";

const SESSION_KEY = "isSidebarOpen";
const SIDEBAR_PREFERENCE_KEY = SIDEBAR_OPEN;

const disableWheelOnNumberInput = () => {
  document.addEventListener(
    "wheel",
    (e) => {
      if (e.target.type === "number" && document.activeElement === e.target) {
        e.preventDefault();
      }
    },
    { passive: false }
  );
};

function RackStarApp({ isDarkMode, toggleTheme }) {
  const navigate = useNavigate();
  const { instance, accounts } = useMsal();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));
  const [anchorEl, setAnchorEl] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [profileError, setProfileError] = useState(null);

  // Initialize sidebar state with localStorage as primary and cookie fallback
  // This allows the user's sidebar preference to persist across sessions
  const initializeSidebarState = () => {
    logger.log("🔍 Initializing sidebar state...");

    // First try localStorage (more reliable in dev environments)
    const localStorageValue = localStorage.getItem(SIDEBAR_PREFERENCE_KEY);
    logger.log(
      `LocalStorage value for ${SIDEBAR_PREFERENCE_KEY}:`,
      localStorageValue
    );

    if (localStorageValue !== null) {
      const boolValue = localStorageValue === "true";
      logger.log("✅ Using localStorage value:", boolValue);
      return boolValue;
    }

    // Fallback to cookies
    const cookieValue = cookieService.getBooleanPreference(
      SIDEBAR_PREFERENCE_KEY
    );
    logger.log(`Cookie value for ${SIDEBAR_PREFERENCE_KEY}:`, cookieValue);

    if (cookieValue !== null && cookieValue !== undefined) {
      logger.log(
        "✅ Using cookie value (migrating to localStorage):",
        cookieValue
      );
      // Migrate to localStorage for better reliability
      localStorage.setItem(SIDEBAR_PREFERENCE_KEY, cookieValue.toString());
      return cookieValue;
    }

    // Check old localStorage key for migration
    const oldLocalStorageValue = localStorage.getItem(SESSION_KEY);
    if (oldLocalStorageValue !== null) {
      const boolValue =
        oldLocalStorageValue === "true" || oldLocalStorageValue === true;
      logger.log("🔄 Migrating from old localStorage key:", boolValue);
      localStorage.setItem(SIDEBAR_PREFERENCE_KEY, boolValue.toString());
      localStorage.removeItem(SESSION_KEY);
      return boolValue;
    }

    // Default to collapsed (false) for new users
    logger.log("📝 Using default value: false");
    return false;
  };

  const [isSidebarOpen, setIsSidebarOpen] = useState(initializeSidebarState);
  const open = Boolean(anchorEl);
  const location = useLocation();

  // Memoized fetch function with AbortController
  const fetchUserInfo = useCallback(async () => {
    if (accounts.length === 0) {
      logger.log("No accounts available to fetch user info");
      setProfileError("Please log in to view profile information.");
      return;
    }

    try {
      logger.log("🔄 Acquiring token silently...");
      const tokenResponse = await instance.acquireTokenSilent({
        ...loginRequest,
        account: accounts[0],
      });
      logger.log("✅ Token acquired:", tokenResponse.accessToken.slice(0, 20) + "...");

      logger.log("🔄 Fetching user info from Microsoft Graph...");
      const response = await axios.get("https://graph.microsoft.com/v1.0/me", {
        headers: { Authorization: `Bearer ${tokenResponse.accessToken}` },
      });
      logger.log("✅ User info fetched:", response.data);

      const profile = {
        displayName: response.data.displayName,
        givenName: response.data.givenName,
        mail: response.data.mail,
        surname: response.data.surname,
      };
      setItem("UserProfile", profile);
      setUserProfile(profile);
      setProfileError(null); // Clear any previous error on success
    } catch (error) {
      if (axios.isCancel(error)) {
        logger.log("User info fetch cancelled");
        return;
      }

      // Handle InteractionRequiredAuthError - MFA/Azure AD session expired
      if (error instanceof InteractionRequiredAuthError) {
        logger.warn("⚠️ Profile: Session expired (interaction required) - user needs to re-login", {
          errorCode: error.errorCode,
          errorMessage: error.errorMessage,
        });
        setProfileError("Your session has expired. Please refresh the page to log in again.");
        return;
      }

      logger.error("❌ Error fetching user info:", error);
      setProfileError(`Failed to fetch profile: ${error.message}`);
    }
  }, [accounts, instance]);

  // Fetch data on mount with cleanup
  useEffect(() => {
    fetchUserInfo();
  }, [fetchUserInfo]);

  useEffect(() => {
    cancelAllRequests(); // cancel ongoing requests on route change
  }, [location]);

  useEffect(() => {
    disableWheelOnNumberInput();
  }, []);

  const handleMenuOpen = (event) => setAnchorEl(event.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);

  const handleLogout = () => logoutUser();

  const toggleSidebar = (state) => {
    var tValue = !isSidebarOpen;

    if (typeof state === "boolean") {
      tValue = state;
    }

    logger.log(`🔄 Toggling sidebar to: ${tValue}`);
    setIsSidebarOpen(tValue);

    // Save to both localStorage (primary) and cookies (fallback)
    localStorage.setItem(SIDEBAR_PREFERENCE_KEY, tValue.toString());
    cookieService.setPreference(SIDEBAR_PREFERENCE_KEY, tValue);
    logger.log(
      `💾 Saved sidebar preference to localStorage and cookies: ${tValue}`
    );
  };

  return (
    <Box sx={{ display: "flex" }}>
      <CssBaseline />
      <ConfirmDialogManager />
      <Box sx={{ width: "100%" }}>
        <AppBar
          position="fixed"
          sx={{
            zIndex: (theme) => theme.zIndex.drawer + 1,
            paddingRight: "0 !important",
          }}
        >
          <Toolbar
            sx={{
              minHeight: "60px",
              display: "flex",
              justifyContent: "space-between",
              gap: 2,
              "@media (max-width:365px)": {
                gap: 1,
                paddingLeft: "8px",
              },
              width: "100%",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <IconButton
                onClick={toggleSidebar}
                aria-label={isSidebarOpen ? "Close sidebar" : "Open sidebar"}
              >
                {isSidebarOpen ? (
                  <MenuOpenIcon sx={{ color: "white" }} />
                ) : (
                  <MenuIcon sx={{ color: "white" }} />
                )}
              </IconButton>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  cursor: "pointer",
                }}
                onClick={() => navigate(ROUTES.home)}
                role="link"
                aria-label="Navigate to home"
                tabIndex={0}
              >
                <img
                  src={isDarkMode ? RackStarLogoDark : RackStarLogoLight}
                  alt="RackStar Logo"
                  style={{ height: "32px" }}
                />
                <Typography
                  variant="subtitle2"
                  sx={{
                    marginLeft: "8px",
                    fontWeight: "bold",
                    fontSize: "12px",
                  }}
                >
                  v{APP_VERSION}{(import.meta.env.VITE_ENVIRONMENT === "Dev" || import.meta.env.VITE_ENVIRONMENT === "Test") ? ` ${import.meta.env.VITE_ENVIRONMENT}` : ""}
                </Typography>
              </Box>
            </Box>
            {!isSmallScreen && <Profile userProfile={userProfile} profileError={profileError} />}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                "@media (max-width:365px)": {
                  gap: 1,
                },
                justifyContent: "end",
                width: isSmallScreen ? "100%" : "auto",
              }}
            >
              <GlobalSearchBar />
              {!isSmallScreen && <FontSizeControl />}
              <Tooltip
                title={isDarkMode ? "Light Mode" : "Dark Mode"}
                placement="left"
                arrow
                enterDelay={0}
                leaveDelay={0}
              >
                <Box
                  component="span"
                  sx={{ display: "flex", alignItems: "center" }}
                >
                  <DarkModeSwitch
                    onChange={toggleTheme}
                    checked={!isDarkMode}
                    size={28}
                    moonColor="white"
                    sunColor="white"
                    aria-label="Toggle dark mode"
                  />
                </Box>
              </Tooltip>
              {!isSmallScreen && (
                <div
                  onClick={handleLogout}
                  title="Logout"
                  style={{ padding: "5px 15px 0px 10px", cursor: "pointer" }}
                  role="button"
                  tabIndex={0}
                  aria-label="Log out"
                  onKeyDown={(e) => e.key === "Enter" && handleLogout()}
                >
                  <LogOutIcon />
                </div>
              )}
              {!isSmallScreen && <AuthStatus compact={true} />}
              {isSmallScreen && (
                <>
                  <IconButton onClick={handleMenuOpen} size="small">
                    <PersonIcon
                      alt="Profile"
                      sx={{ width: 32, height: 32, color: "white" }}
                    />
                  </IconButton>
                  <Menu
                    anchorEl={anchorEl}
                    open={open}
                    onClose={handleMenuClose}
                    PaperProps={{
                      elevation: 3,
                      sx: {
                        mt: 1.5,
                        borderRadius: 2,
                        overflow: "visible",
                      },
                    }}
                    anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
                    transformOrigin={{ horizontal: "right", vertical: "top" }}
                  >
                    {/* Header */}
                    <Box px={2} pt={1} pb={1}>
                      <Box display="flex" alignItems="center">
                        <Box ml={1}>
                          <Typography fontWeight="bold">
                            {userProfile?.displayName}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {userProfile?.mail}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>

                    <Divider />

                    {/* Menu Items */}
                    <MenuItem>
                      <FontSizeControl />
                    </MenuItem>
                    <MenuItem
                      onClick={() => {
                        handleMenuClose();
                        handleLogout();
                      }}
                    >
                      <ListItemIcon>
                        <Logout fontSize="small" />
                      </ListItemIcon>
                      Logout
                    </MenuItem>
                  </Menu>
                </>
              )}
            </Box>
            {/* Profile Avatar */}
          </Toolbar>
        </AppBar>
        <Box sx={{ display: "flex" }}>
          <Sidebar
            isSidebarOpen={isSidebarOpen}
            toggleSidebar={toggleSidebar}
          />
          <Box component="main" sx={{ flexGrow: 1, p: 3 }}>
            <Toolbar />
            <CustomRoutes />
          </Box>
        </Box>
      </Box>

      <AIChatBot />
    </Box>
  );
}

export default RackStarApp;
